import * as PIXI from 'pixi.js';
import type {
    VideoFrameSource,
    VideoFramesProvider,
    VideoFramesProviderEvent,
    VideoFramesProviderFactory,
    VideoFramesProviderListener,
    VideoFramesProviderListenerOptions,
    VideoFramesProviderParams,
    VideoReadyState,
} from './VideoFramesProvider';

const DEBUG_NAME_ATTRIBUTE = 'data-name-for-debugging';

/**
 * The default {@link VideoFramesProvider}: a `<video>` element decoded by the browser. This is the
 * only place in luminary that is allowed to talk to the `HTMLVideoElement` interface — everything
 * else goes through {@link VideoFramesProvider}.
 *
 * NOTE: DO NOT mute before load, as Chrome bugs and creates a WebGL error
 *       https://bugs.chromium.org/p/chromium/issues/detail?id=898550
 *
 * Preloading:
 * The only reason we create the video element ourselves is to avoid PIXI setting preload="auto".
 * Preloading causes seeking to be super slow for some videos.
 * It requires more network and processing, especially with trimmed media, where
 * it seems that browsers try to load all the file instead of seeking.
 */
export class HtmlVideoFramesProvider implements VideoFramesProvider {
    #videoElement: HTMLVideoElement;
    #sourceUrl: string;
    #mimeType: string;

    constructor({ sourceUrl, mimeType }: VideoFramesProviderParams) {
        this.#sourceUrl = sourceUrl;
        this.#mimeType = mimeType;

        this.#videoElement = (() => {
            const videoElement = document.createElement('video');
            videoElement.setAttribute('preload', 'none');
            videoElement.setAttribute('webkit-playsinline', '');
            videoElement.setAttribute('playsinline', '');

            PIXI.BaseImageResource.crossOrigin(videoElement, sourceUrl);

            const sourceElement = document.createElement('source');
            sourceElement.src = sourceUrl;
            sourceElement.type = mimeType;
            videoElement.appendChild(sourceElement);

            return videoElement;
        })();
        this.debugName = 'original';
    }

    get currentFrame(): VideoFrameSource | null {
        return this.#videoElement;
    }

    get videoWidth(): number {
        return this.#videoElement.videoWidth;
    }

    get videoHeight(): number {
        return this.#videoElement.videoHeight;
    }

    get duration(): number | null {
        const duration = this.#videoElement.duration;
        return Number.isNaN(duration) ? null : duration;
    }

    get isLoaded(): boolean {
        return this.duration !== null;
    }

    get readyState(): VideoReadyState {
        return this.#videoElement.readyState as VideoReadyState;
    }

    get seeking(): boolean {
        return this.#videoElement.seeking;
    }

    get paused(): boolean {
        return this.#videoElement.paused;
    }

    get sourceUrl(): string {
        return this.#sourceUrl;
    }

    get audioElement(): HTMLMediaElement | null {
        return this.#videoElement;
    }

    get currentTime(): number {
        return this.#videoElement.currentTime;
    }

    set currentTime(time: number) {
        this.#videoElement.currentTime = time;
    }

    get playbackRate(): number {
        return this.#videoElement.playbackRate;
    }

    set playbackRate(rate: number) {
        this.#videoElement.playbackRate = rate;
    }

    get muted(): boolean {
        return this.#videoElement.muted;
    }

    set muted(muted: boolean) {
        this.#videoElement.muted = muted;
    }

    get debugName(): string {
        return this.#videoElement.getAttribute(DEBUG_NAME_ATTRIBUTE) ?? '';
    }

    set debugName(name: string) {
        this.#videoElement.setAttribute(DEBUG_NAME_ATTRIBUTE, name);
    }

    load(): void {
        this.#videoElement.load();
    }

    async play(): Promise<void> {
        return this.#videoElement.play();
    }

    pause(): void {
        this.#videoElement.pause();
    }

    clone(): VideoFramesProvider {
        const clone = new HtmlVideoFramesProvider({
            sourceUrl: this.#sourceUrl,
            mimeType: this.#mimeType,
        });
        clone.load();
        return clone;
    }

    destroy(): void {
        this.#videoElement.pause();
        this.#videoElement.src = '';
        this.#videoElement.load();
    }

    /**
     * Listeners are registered in the capture phase because a failing `<source>` fires its `error`
     * event at the child element and media events do not bubble; without capture a load failure
     * would never reach the listener.
     */
    addEventListener(
        type: VideoFramesProviderEvent,
        listener: VideoFramesProviderListener,
        options?: VideoFramesProviderListenerOptions,
    ): void {
        this.#videoElement.addEventListener(type, listener, {
            ...options,
            capture: true,
        });
    }

    removeEventListener(
        type: VideoFramesProviderEvent,
        listener: VideoFramesProviderListener,
    ): void {
        this.#videoElement.removeEventListener(type, listener, { capture: true });
    }
}

export const createHtmlVideoFramesProvider: VideoFramesProviderFactory = (params) =>
    new HtmlVideoFramesProvider(params);
